import { toastError, toastSuccess } from "../toast.js";
import { adminRequest as request, type ApiTarget } from "./request.js";
import type { DeliveryDeltaView, TermPricesView, VenueForecastView,
  FinanceView,
  OwnAdmissionView,
  OwnDelegationsView,
  OwnEconomicsView,
  OwnPoolDetail,
  OwnPoolSettings,
  OwnSupplyView,
  McpUsageView,
  NetworkTab,
  NetworkTabView,
  NetworkView,
  PoolsView,
  ProviderPoolsDetail,
  OverviewView,
  ActivePriceRow,
  AdminAccount,
  AdminAccountProfile,
  AdminAlert,
  AdminIdentity,
  AdminLedgerView,
  AdminDepositView,
  AdminOrderDetail,
  AdminNodeKey,
  AdminSubscription,
  DepthSource,
  JustLendView,
  AdminWalletsView,
  ProviderAccount,
  ProviderPolicyPatch,
  TopupSettings,
  QueuesView,
  AdminOrder,
  AuditRow,
  B2bContract,
  DayPartScheduleVersion,
  DayPartWindow,
  DemandReport,
  FloorVersion,
  FundWallet,
  LadderNeutralDefaults,
  LadderVersion,
  OrderBookView,
  PoolLink,
  PriceVersion,
  RouterFeeVersion,
  RouterFeeView,
  RouterFeeSchedule,
  RouterFeeSchedulesView,
  RouterFeeTier,
  PromoCode,
  PromoDropState,
  PromoDropsView,
  PromoCohort,
  PromoFunnel,
  FunnelPeriod,
  PromoSendQueued,
  PromoTokenPatch,
  PromoTokenSettings,
  PromoTokenView,
  PromoRedemption,
  SettlementStatement,
  TransferVersion,
} from "./types.js";

/**
 * Every call this panel can make, in one file, split into reads and mutations.
 *
 * THE SPLIT IS THE POINT. `adminReads` is what a `viewer` may do and `adminWrites` is what an
 * `editor` may do (b2b-pricing.md §4), and a screen imports the half it needs. It is not a
 * security boundary — the server's `guard.admin` is — but it makes "did this screen just call
 * a mutation?" a question a reader can answer by looking at the imports.
 *
 * Transport is `@tenergy/web-core`'s `request`: the session cookie travels on `credentials:
 * "include"` and every unsafe method carries `X-CSRF-Token` from the session the panel restored.
 * No API key is used here, and none can be: a key is a machine credential and this is a browser
 * (`apps/api/src/plugins/auth.ts`).
 */

/** The watch drops' two groups (owner 2026-10-24): payers to a pay-in address, and venues' account clients. */
export type WatchDropGroup = "payers" | "clients";
export interface WatchDropSwitch {
  enabled: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface PageQuery {
  limit?: number;
}

function query(input: Record<string, string | number | undefined>): Record<string, string | number | undefined> {
  return input;
}

export const adminReads = {
  /**
   * WHICH RIGHT THIS SESSION HOLDS. The first call the app makes after restoring a session, and
   * the only thing that decides which chrome is rendered. `role: null` is "no admin access".
   */
  me: (signal?: AbortSignal) =>
    request<AdminIdentity>("/admin/me", signal ? { signal } : {}),

  activePrices: (brandId?: string) =>
    request<{ data: ActivePriceRow[] }>("/admin/prices", {
      query: query({ brand_id: brandId }),
    }),

  priceVersions: (input: PageQuery & { brandId?: string } = {}) =>
    request<{ data: PriceVersion[] }>("/admin/prices/versions", {
      query: query({ brand_id: input.brandId, limit: input.limit }),
    }),

  floorPrices: (input: PageQuery = {}) =>
    request<{ active: FloorVersion[]; versions: FloorVersion[] }>("/admin/prices/floor", {
      query: query({ limit: input.limit }),
    }),

  routerFee: (input: PageQuery = {}) =>
    request<RouterFeeView>("/admin/router-fee", { query: query({ limit: input.limit }) }),

  routerFeeSchedules: () => request<RouterFeeSchedulesView>("/admin/router-fee/schedules"),

  promoCodes: (input: PageQuery = {}) =>
    request<{ items: PromoCode[] }>("/admin/promo-codes", { query: query({ limit: input.limit }) }),

  /** The promo token redeem: settings, today's count, the redemptions (viewer). */
  promoToken: () => request<PromoTokenView>("/admin/promo-token"),
  /** The automatic tenergy.dev drops: the switch, the next sender, 24 h counts, the latest drops and skips (viewer). */
  promoDrops: (target?: ApiTarget) => request<PromoDropsView>("/admin/promo-token/drops", target === undefined ? {} : { target }),

  /** tenergy.app's funnel for the wallets sent to in the period (mainnet). */
  promoFunnel: (period: FunnelPeriod) => request<PromoFunnel>("/admin/promo-token/funnel", { query: { ...period }, target: "mainnet" }),

  /** The addresses this network's token was sent to in the period. */
  promoCohort: (period: FunnelPeriod, target: ApiTarget) => request<PromoCohort>("/admin/promo-token/drops/cohort", { query: { ...period }, target }),

  /**
   * tenergy.dev's funnel on mainnet for a cohort read from Nile (a read; POST carries the addresses). Any size: sent in
   * chunks of DEV_FUNNEL_CHUNK, one after the other, and the counts summed (each address is in one chunk only).
   */
  promoDevFunnel: async (period: FunnelPeriod, addresses: string[]): Promise<PromoFunnel> => {
    const unique = [...new Set(addresses)];
    const total: PromoFunnel = { token: "dev", token_id: null, ...period, sent: 0, claimed: 0, paying: 0, prior: 0 };
    for (let i = 0; i === 0 || i < unique.length; i += DEV_FUNNEL_CHUNK) {
      const part = await request<PromoFunnel>("/admin/promo-token/funnel/dev", {
        method: "POST",
        body: { ...period, addresses: unique.slice(i, i + DEV_FUNNEL_CHUNK) },
        target: "mainnet",
      });
      total.sent += part.sent;
      total.claimed += part.claimed;
      total.paying += part.paying;
      total.prior += part.prior;
    }
    return total;
  },

  promoRedemptions: (id: string) =>
    request<{ items: PromoRedemption[] }>(`/admin/promo-codes/${encodeURIComponent(id)}/redemptions`),

  transferPrices: (input: PageQuery = {}) =>
    request<{ active: TransferVersion | null; versions: TransferVersion[] }>(
      "/admin/prices/transfer",
      { query: query({ limit: input.limit }) },
    ),

  ladderParameters: (input: PageQuery & { brandId?: string } = {}) =>
    request<{
      active: LadderVersion | null;
      neutral_defaults: LadderNeutralDefaults;
      versions: LadderVersion[];
    }>("/admin/prices/ladder", { query: query({ brand_id: input.brandId, limit: input.limit }) }),

  /** The day-part schedule in force for the brand, and every version (pricing.md §1.2b). */
  dayParts: (input: PageQuery & { brandId?: string } = {}) =>
    request<{ active: DayPartScheduleVersion | null; versions: DayPartScheduleVersion[] }>(
      "/admin/prices/day-parts",
      { query: query({ brand_id: input.brandId, limit: input.limit }) },
    ),

  contracts: (input: { accountId?: string; status?: string; limit?: number } = {}) =>
    request<{ data: B2bContract[] }>("/admin/contracts", {
      query: query({ account_id: input.accountId, status: input.status, limit: input.limit }),
    }),

  orders: (
    input: { status?: string; receiver?: string; accountId?: string; kind?: string; limit?: number } = {},
  ) =>
    request<{ data: AdminOrder[]; next_cursor: string | null }>("/admin/orders", {
      query: query({
        status: input.status,
        receiver: input.receiver,
        account_id: input.accountId,
        kind: input.kind,
        limit: input.limit,
      }),
    }),

  /** Every account's subscriptions, newest first (viewer). Read only. */
  subscriptions: (input: { status?: string; accountId?: string; limit?: number; cursor?: string } = {}) =>
    request<{ data: AdminSubscription[]; next_cursor: string | null }>("/admin/subscriptions", {
      query: query({ status: input.status, account_id: input.accountId, limit: input.limit, cursor: input.cursor }),
    }),

  /** Every account's node keys, newest first, the id masked (viewer). Read only. */
  /** Customer withdrawals by status, oldest first (wallet-accounts.md). */
  withdrawals: (status: string) =>
    request<{ data: AdminWithdrawal[] }>("/admin/withdrawals", { query: query({ status }) }),

  nodeKeys: (input: { accountId?: string; limit?: number; cursor?: string } = {}) =>
    request<{ data: AdminNodeKey[]; next_cursor: string | null }>("/admin/node-keys", {
      query: query({ account_id: input.accountId, limit: input.limit, cursor: input.cursor }),
    }),

  accounts: (
    input: { address?: string; brandId?: string; status?: string; accountId?: string; limit?: number } = {},
  ) =>
    request<{ data: AdminAccount[]; next_cursor: string | null }>("/admin/accounts", {
      query: query({
        address: input.address,
        brand_id: input.brandId,
        status: input.status,
        account_id: input.accountId,
        limit: input.limit,
      }),
    }),

  fundWallets: () => request<{ data: FundWallet[] }>("/admin/fund/wallets"),

  poolLinks: () => request<{ data: PoolLink[] }>("/admin/pool/links"),

  alerts: (input: PageQuery = {}) =>
    request<{ as_of: string; data: AdminAlert[] }>("/admin/alerts", {
      query: query({ limit: input.limit }),
    }),

  audit: (
    input: { action?: string; targetType?: string; targetId?: string; limit?: number } = {},
  ) =>
    request<{ data: AuditRow[]; next_cursor: string | null }>("/admin/audit", {
      query: query({
        action: input.action,
        target_type: input.targetType,
        target_id: input.targetId,
        limit: input.limit,
      }),
    }),

  demand: (input: { from?: string; to?: string } = {}) =>
    request<DemandReport>("/fund/demand", { query: query({ from: input.from, to: input.to }) }),

  settlement: (input: { from?: string; to?: string } = {}) =>
    request<SettlementStatement>("/fund/settlement", {
      query: query({ from: input.from, to: input.to }),
    }),

  /**
   * The PUBLIC ladder. There is no admin variant: `toPublicLevels` in the api copies three
   * fields and a source id is not one of them (order-book.md §2, "provider names are never
   * published"), so the class — `instant` / `market` / `deep` — is the finest attribution any
   * endpoint offers. The screen says so rather than implying the columns are missing by accident.
   */
  orderBook: (input: { resource?: string; tier?: string; amount?: number } = {}) =>
    request<OrderBookView>("/orderbook", {
      query: query({ resource: input.resource, tier: input.tier, amount: input.amount }),
    }),

  // ---- operator reads (apps/api/src/routes/admin-ops.ts), all under the viewer guard -------

  /** BullMQ counts per worker queue; `available: false` when the API could not count. */
  queues: () => request<QueuesView>("/admin/queues"),

  /** The Monitoring page: router, providers and business numbers in one read (viewer, cached 30 s). */
  overview: () => request<OverviewView>("/admin/overview"),
  finance: (period: FinanceView["period"]) => request<FinanceView>(`/admin/finance?period=${period}`),
  ownSupply: () => request<OwnSupplyView>("/admin/own-supply"),
  ownPool: (address: string) => request<OwnPoolDetail>(`/admin/own-supply/pools/${encodeURIComponent(address)}`),
  ownDelegations: (state: "active" | "all") => request<OwnDelegationsView>(`/admin/own-supply/delegations?state=${state}`),
  ownAdmission: (days: number) => request<OwnAdmissionView>(`/admin/own-supply/admission?days=${days}`),
  ownEconomics: (days: 7 | 30) => request<OwnEconomicsView>(`/admin/own-supply/economics?days=${days}`),

  /** The chain analyzer's collector and by-day table (viewer). */
  network: () => request<NetworkView>("/admin/network"),

  /** The providers' pools from the analyzer's microtron (viewer, cached 15 s). */
  pools: () => request<PoolsView>("/admin/pools"),
  providerPools: (provider: string) => request<ProviderPoolsDetail>(`/admin/pools/${encodeURIComponent(provider)}`),

  /** One Network tab for a window (viewer; the API caches each window for 30 s). */
  /** The watch-driven promo drops' switch (viewer). */
  watchDrops: () =>
    request<Record<WatchDropGroup, WatchDropSwitch> & { exclude_venues: string[]; venues: string[] }>("/admin/network/watch/drops"),
  networkTab: (tab: NetworkTab, input: { window: string; from?: string | undefined; to?: string | undefined; address?: string | undefined; owners?: string | undefined }) =>
    request<NetworkTabView>(`/admin/network/tabs/${tab}`, { query: query(input) }),

  /** MCP usage per UTC day, top clients and tools (viewer). */
  mcpUsage: (days = 30) => request<McpUsageView>("/admin/mcp-usage", { query: query({ days }) }),

  /** `provider_accounts`: health, credit and top-up policy. */
  providers: () => request<{ as_of: string; data: ProviderAccount[] }>("/admin/providers"),

  /** The next 24 h forecast of every venue series with a minute history. */
  venueForecasts: () => request<VenueForecastView>("/admin/forecast/venues"),

  /** Each venue's newest answer per resource and rental term: the term matrix. */
  termPrices: () => request<TermPricesView>("/admin/forecast/terms"),

  /** Delivered vs ordered per venue over 7 or 30 days. */
  deliveryDelta: (days: 7 | 30) => request<DeliveryDeltaView>(`/admin/providers/delivery?days=${days}`),

  /** The global "Minimum top-up" (null = the worker default). */
  topupSettings: () => request<TopupSettings>("/admin/topup-settings"),

  /** Our own addresses with live chain data, and customer balances held (viewer). */
  wallets: () => request<AdminWalletsView>("/admin/wallets"),

  /** Open and overdue JustLend rentals, unread refunds, recent settled. */
  justLend: () => request<JustLendView>("/admin/justlend"),

  /** One order end to end: fills, allocations, chain transactions, events. */
  order: (id: string) => request<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(id)}`),

  /** One account's ledger statement, newest first. */
  accountLedger: (id: string, input: { limit?: number; cursor?: string } = {}) =>
    request<AdminLedgerView>(`/admin/accounts/${encodeURIComponent(id)}/ledger`, {
      query: query({ limit: input.limit, cursor: input.cursor }),
    }),

  /** One customer on one screen: facts, money, deposits, orders, receivers, integration, note. */
  accountProfile: (id: string) =>
    request<AdminAccountProfile>(`/admin/accounts/${encodeURIComponent(id)}/profile`),

  /** One account's deposit addresses: active, history, rotation allowance. */
  depositAddresses: (id: string) =>
    request<AdminDepositView>(`/admin/accounts/${encodeURIComponent(id)}/deposit-addresses`),

  /** The ladder's sources: `supply_depth` rows with the source id the public book hides. */
  depthSources: (input: { resource?: string; tier?: string } = {}) =>
    request<{ as_of: string; fresh_for_seconds: number; data: DepthSource[] }>("/admin/orderbook/sources", {
      query: query({ resource: input.resource, tier: input.tier }),
    }),
};

// ---- mutations: `editor` only, every one with a mandatory reason ------------------------------

export interface PricePublish {
  brand_id?: string;
  resource: string;
  tier: string;
  day_part?: string;
  base_price_sun_per_unit: number;
  day_part_factor_bp?: number;
  brand_markup_bp?: number;
  volume_grid?: Array<{ min_amount: number; price_sun_per_unit: number }>;
  min_amount?: number;
  max_amount?: number;
  sales_paused?: boolean;
  visible?: boolean;
  effective_at?: string;
  reason: string;
}

export interface FloorPublish {
  resource: string;
  tier: string;
  day_part?: string;
  price_centi_sun_per_unit: number;
  mode?: "live_then_static" | "static_only";
  effective_from?: string;
  reason: string;
}

export interface RouterFeePublish {
  /** Basis points of the provider price, 0..2,000 (0..20 %). */
  fee_bps: number;
  effective_from?: string;
  reason: string;
}

export interface RouterFeeSchedulePublish {
  /** The whole table: ascending `from_sun`, the first 0; `fee_bps` 0..2,000 (0..20 %). */
  tiers: RouterFeeTier[];
  effective_from?: string;
  reason: string;
}

export interface PromoCodeCreate {
  /** Omitted = the server generates 8 chars `[A-Z0-9]`. */
  code?: string;
  fee_bps: number;
  duration_days: number;
  starts_at?: string;
  ends_at?: string;
  max_uses?: number;
  note: string;
}

export interface PromoCodePatch {
  active?: boolean;
  ends_at?: string | null;
  max_uses?: number | null;
  note?: string;
}

export interface TransferPublish {
  fund_wallet_id?: string;
  sun_per_energy_day_bp: number;
  effective_from?: string;
  reason: string;
}

export interface LadderPublish {
  brand_id?: string;
  utilisation_bands?: Array<{ above_bps: number; surcharge_bps: number }>;
  margin_instant_bp?: number;
  margin_market_bp?: number;
  margin_deep_bp?: number;
  hold_back_bp?: number;
  min_level_amount?: number;
  max_levels?: number;
  slippage_bp?: number;
  effective_from?: string;
  reason: string;
}

export interface DayPartSchedulePublish {
  brand_id?: string;
  windows: DayPartWindow[];
  effective_from?: string;
  reason: string;
}

export interface ContractCreate {
  account_id: string;
  format: "reserve" | "on_demand" | "on_demand_guaranteed";
  price_bp: number;
  reserve_amount?: number;
  reserve_addresses?: string[];
  term_from?: string;
  term_to?: string;
  overflow_daily_cap_sun?: number;
  offer_ladder_beyond_cap?: boolean;
  reason: string;
}

export interface FundWalletRegister {
  address: string;
  permission_id: number;
  supplier_id: string;
  label?: string;
  role?: "fleet" | "flash" | "wholesale_out" | "pool";
  reason: string;
}

export interface PoolLinkCreate {
  pool_wallet: string;
  fleet_wallet: string;
  lent_sun: string;
  operator: "platform" | "external";
  min_interval_s: number;
  trigger_locked_bps: number;
  daily_bandwidth_budget: number;
  reason: string;
}

export interface AlertAcknowledge {
  kind: string;
  target: string;
  reason: string;
}

export interface LedgerAdjustment {
  account_id: string;
  amount_sun: number;
  currency: "TRX" | "USDT";
  reference_id?: string;
  reason: string;
}

/**
 * Every mutation toasts: success with the action's name, failure with the API's slug and the
 * request id (`toast.tsx`). The promise still rejects, so a form keeps its inline field error.
 */
function announced<A extends unknown[], T>(label: string, call: (...args: A) => Promise<T>) {
  return async (...args: A): Promise<T> => {
    try {
      const result = await call(...args);
      toastSuccess(label, "Written to the audit log.");
      return result;
    } catch (error) {
      toastError(`${label} — failed`, error);
      throw error;
    }
  };
}

const rawWrites = {
  publishPrice: (body: PricePublish) =>
    request<{ id: string; effective_at: string }>("/admin/prices", { method: "POST", body }),

  publishFloor: (body: FloorPublish) =>
    request<FloorVersion>("/admin/prices/floor", { method: "POST", body }),

  publishRouterFee: (body: RouterFeePublish) =>
    request<RouterFeeVersion>("/admin/router-fee", { method: "POST", body }),

  publishRouterFeeSchedule: (body: RouterFeeSchedulePublish) =>
    request<RouterFeeSchedule>("/admin/router-fee/schedules", { method: "POST", body }),

  createPromoCode: (body: PromoCodeCreate) =>
    request<PromoCode>("/admin/promo-codes", { method: "POST", body }),

  /** The promo token's cap per UTC day and window (editor, audited). */
  updatePromoToken: (body: PromoTokenPatch) => request<PromoTokenSettings>("/admin/promo-token", { method: "PATCH", body }),
  /** Automatic drops on or off (editor, audited). */
  setPromoDrops: (body: { enabled: boolean }, target?: ApiTarget) =>
    request<PromoDropState>("/admin/promo-token/drops", { method: "PATCH", body, ...(target === undefined ? {} : { target }) }),

  /** The watch-driven promo drops' switch of one group (editor, audited). */
  setWatchDrops: (body: { group: WatchDropGroup; enabled: boolean }) =>
    request<WatchDropSwitch>("/admin/network/watch/drops", { method: "PATCH", body }),
  /** The venues whose clients the watch drops leave alone (editor, audited). */
  setWatchExcludeVenues: (body: { exclude_venues: string[] }) =>
    request<{ exclude_venues: string[] }>("/admin/network/watch/drops/venues", { method: "PATCH", body }),

  /** Adds a pay-in wallet or a client deposit to the chain watch (editor, audited; docs/product/chain-watch.md). */
  addWatchAddress: (body: { address: string; kind: "payin" | "deposit"; venue: string; note: string }) =>
    request<{ address: string; kind: string; venue: string; note: string }>("/admin/network/watch", { method: "POST", body }),

  sendPromoDrop: (body: { address: string; tokens: number }, target?: ApiTarget) =>
    request<PromoSendQueued>("/admin/promo-token/drops/send", { method: "POST", body, ...(target === undefined ? {} : { target }) }),

  updatePromoCode: (id: string, body: PromoCodePatch) =>
    request<PromoCode>(`/admin/promo-codes/${encodeURIComponent(id)}`, { method: "PATCH", body }),

  publishTransfer: (body: TransferPublish) =>
    request<TransferVersion>("/admin/prices/transfer", { method: "POST", body }),

  publishLadder: (body: LadderPublish) =>
    request<LadderVersion>("/admin/prices/ladder", { method: "POST", body }),

  publishDayParts: (body: DayPartSchedulePublish) =>
    request<DayPartScheduleVersion>("/admin/prices/day-parts", { method: "POST", body }),

  createContract: (body: ContractCreate) =>
    request<B2bContract>("/admin/contracts", { method: "POST", body }),

  closeContract: (id: string, body: { status?: "closed" | "suspended"; reason: string }) =>
    request<B2bContract>(`/admin/contracts/${encodeURIComponent(id)}/close`, {
      method: "POST",
      body,
    }),

  registerFundWallet: (body: FundWalletRegister) =>
    request<{ address: string; status: string; role: string; supplier_id: string | null }>(
      "/admin/fund/wallets",
      { method: "POST", body },
    ),

  createPoolLink: (body: PoolLinkCreate) =>
    request<{ id: string; state: string; status: string }>("/admin/pool/links", {
      method: "POST",
      body,
    }),

  setPoolLinkStatus: (id: string, body: { status: "active" | "paused"; reason: string }) =>
    request<{ id: string; status: string; state: string }>(
      `/admin/pool/links/${encodeURIComponent(id)}/status`,
      { method: "POST", body },
    ),

  /** A pool's own settings (target window, supplier rate). Editor, audited; no chain action. */
  updateOwnPoolSettings: (
    address: string,
    body: { target_window_minutes?: number; supplier_rate_centi_sun?: number | null; reason?: string },
  ) => request<OwnPoolSettings>(`/admin/own-supply/pools/${encodeURIComponent(address)}/settings`, { method: "PATCH", body }),

  /** Set or clear (null) the global "Minimum top-up" and "Low-water by default". Editor, audited. */
  updateTopupSettings: (body: { min_transfer_sun?: number | null; default_low_water_sun?: number | null }) =>
    request<TopupSettings>("/admin/topup-settings", { method: "PATCH", body }),

  updateProviderPolicy: (provider: string, body: ProviderPolicyPatch) =>
    request<{
      provider: string;
      enabled: boolean;
      low_water_sun: number | null;
      daily_topup_cap_sun: number | null;
      topup_target_sun: number | null;
    }>(
      `/admin/providers/${encodeURIComponent(provider)}`,
      { method: "PATCH", body },
    ),

  saveAccountNote: (id: string, body: { note: string | null }) =>
    request<{ account_id: string; note: string | null; updated_at: string }>(
      `/admin/accounts/${encodeURIComponent(id)}/note`,
      { method: "PATCH", body },
    ),

  adjustLedger: (body: LedgerAdjustment) =>
    request<{ journal_id: string; balance_sun: number }>("/admin/ledger/adjustment", {
      method: "POST",
      body,
    }),

  rotateDepositAddress: (id: string, body: { reason: string }) =>
    request<Pick<AdminDepositView, "account_id" | "active" | "allowance">>(
      `/admin/accounts/${encodeURIComponent(id)}/deposit-address/rotate`,
      { method: "POST", body },
    ),

  approveWithdrawal: (id: string) =>
    request<AdminWithdrawal>(`/admin/withdrawals/${encodeURIComponent(id)}/approve`, { method: "POST", body: {} }),

  rejectWithdrawal: (id: string) =>
    request<AdminWithdrawal>(`/admin/withdrawals/${encodeURIComponent(id)}/reject`, { method: "POST", body: {} }),

  acknowledgeAlert: (body: AlertAcknowledge) =>
    request<{ key: string; acknowledged: { by: string | null; at: string; reason: string } }>(
      "/admin/alerts/ack",
      { method: "POST", body },
    ),
};

const WRITE_LABELS: Record<keyof typeof rawWrites, string> = {
  publishPrice: "Retail version published",
  publishFloor: "Floor version published",
  publishRouterFee: "Routing fee published",
  publishRouterFeeSchedule: "Routing fee schedule published",
  createPromoCode: "Promo code created",
  updatePromoCode: "Promo code updated",
  updatePromoToken: "Promo token settings saved",
  setPromoDrops: "Promo drops switched",
  setWatchDrops: "Watch drops switched",
  setWatchExcludeVenues: "Venues left alone saved",
  addWatchAddress: "Address added to the watch",
  sendPromoDrop: "Promo send queued",
  publishTransfer: "Transfer price published",
  publishLadder: "Ladder parameters published",
  publishDayParts: "Day-part schedule published",
  createContract: "Contract created",
  closeContract: "Contract closed",
  registerFundWallet: "Fund wallet registered",
  createPoolLink: "Pool link created",
  setPoolLinkStatus: "Pool link updated",
  updateProviderPolicy: "Provider settings saved",
  updateTopupSettings: "Top-up settings saved",
  updateOwnPoolSettings: "Pool settings saved",
  saveAccountNote: "Note saved",
  adjustLedger: "Ledger adjusted",
  acknowledgeAlert: "Alert acknowledged",
  rotateDepositAddress: "Deposit address rotated",
  approveWithdrawal: "Withdrawal approved",
  rejectWithdrawal: "Withdrawal rejected; the balance was restored",
};

export const adminWrites = {
  publishPrice: announced(WRITE_LABELS.publishPrice, rawWrites.publishPrice),
  publishFloor: announced(WRITE_LABELS.publishFloor, rawWrites.publishFloor),
  publishRouterFee: announced(WRITE_LABELS.publishRouterFee, rawWrites.publishRouterFee),
  publishRouterFeeSchedule: announced(WRITE_LABELS.publishRouterFeeSchedule, rawWrites.publishRouterFeeSchedule),
  createPromoCode: announced(WRITE_LABELS.createPromoCode, rawWrites.createPromoCode),
  updatePromoToken: announced(WRITE_LABELS.updatePromoToken, rawWrites.updatePromoToken),
  setPromoDrops: announced(WRITE_LABELS.setPromoDrops, rawWrites.setPromoDrops),
  setWatchDrops: announced(WRITE_LABELS.setWatchDrops, rawWrites.setWatchDrops),
  setWatchExcludeVenues: announced(WRITE_LABELS.setWatchExcludeVenues, rawWrites.setWatchExcludeVenues),
  addWatchAddress: announced(WRITE_LABELS.addWatchAddress, rawWrites.addWatchAddress),
  sendPromoDrop: announced(WRITE_LABELS.sendPromoDrop, rawWrites.sendPromoDrop),
  updatePromoCode: announced(WRITE_LABELS.updatePromoCode, rawWrites.updatePromoCode),
  publishTransfer: announced(WRITE_LABELS.publishTransfer, rawWrites.publishTransfer),
  publishLadder: announced(WRITE_LABELS.publishLadder, rawWrites.publishLadder),
  publishDayParts: announced(WRITE_LABELS.publishDayParts, rawWrites.publishDayParts),
  createContract: announced(WRITE_LABELS.createContract, rawWrites.createContract),
  closeContract: announced(WRITE_LABELS.closeContract, rawWrites.closeContract),
  registerFundWallet: announced(WRITE_LABELS.registerFundWallet, rawWrites.registerFundWallet),
  createPoolLink: announced(WRITE_LABELS.createPoolLink, rawWrites.createPoolLink),
  setPoolLinkStatus: announced(WRITE_LABELS.setPoolLinkStatus, rawWrites.setPoolLinkStatus),
  updateProviderPolicy: announced(WRITE_LABELS.updateProviderPolicy, rawWrites.updateProviderPolicy),
  updateTopupSettings: announced(WRITE_LABELS.updateTopupSettings, rawWrites.updateTopupSettings),
  updateOwnPoolSettings: announced(WRITE_LABELS.updateOwnPoolSettings, rawWrites.updateOwnPoolSettings),
  saveAccountNote: announced(WRITE_LABELS.saveAccountNote, rawWrites.saveAccountNote),
  adjustLedger: announced(WRITE_LABELS.adjustLedger, rawWrites.adjustLedger),
  acknowledgeAlert: announced(WRITE_LABELS.acknowledgeAlert, rawWrites.acknowledgeAlert),
  rotateDepositAddress: announced(WRITE_LABELS.rotateDepositAddress, rawWrites.rotateDepositAddress),
  approveWithdrawal: announced(WRITE_LABELS.approveWithdrawal, rawWrites.approveWithdrawal),
  rejectWithdrawal: announced(WRITE_LABELS.rejectWithdrawal, rawWrites.rejectWithdrawal),
};

export type AdminReads = typeof adminReads;

/** Addresses per tenergy.dev funnel request; the API refuses more in one. */
export const DEV_FUNNEL_CHUNK = 5_000;
export type AdminWrites = typeof adminWrites;

/** `GET /v1/admin/withdrawals` row. */
export interface AdminWithdrawal {
  id: string;
  account_id: string;
  status: string;
  to_address: string;
  amount_sun: number;
  fee_sun: number;
  txid: string | null;
  approval_reason: string | null;
  created_at: string;
  sent_at: string | null;
  confirmed_at: string | null;
}
